import { IconBuildingCommunity, IconCalendarEvent, IconMapPin } from '@tabler/icons-react';
import { Badge, EmptyState, InlineAlert, formatDate, type BadgeTone } from 'escrowbuild-ui';

import { useOpportunities, useT, type AppTranslationKey } from '@/i18n';
import { type LifecycleStage } from '@/data/domain';

/* =============================================================================
   Investor portal — future opportunities
   -----------------------------------------------------------------------------
   §16 and RF12 both describe this area as deliberately quiet: present, and not
   competing with the investments an investor already holds. So there are no
   figures here at all — not a capital target, not a projected sale, not a share
   on offer.

   That is not restraint for its own sake. The commercialisation and participation
   rules for these developments are out of scope for this version, and a number
   shown before those rules are settled would read as a commitment the company has
   not made.
   ============================================================================= */

/**
 * The stages a future offering is drawn from. Everything from `acquisition`
 * onward is already under way, so it is not an opportunity — it is a development,
 * and it graduates into the platform as a live project rather than being offered
 * from this list.
 */
const FUTURE_STAGES: ReadonlySet<LifecycleStage> = new Set<LifecycleStage>([
  'opportunity',
  'viability',
  'capital-raising',
]);

const STAGE_LABELS: Record<LifecycleStage, AppTranslationKey> = {
  opportunity: 'status.lifecycle.opportunity',
  viability: 'status.lifecycle.viability',
  'capital-raising': 'status.lifecycle.capitalRaising',
  acquisition: 'status.lifecycle.acquisition',
  construction: 'status.lifecycle.construction',
  'sale-and-close': 'status.lifecycle.saleAndClose',
};

const STAGE_TONES: Record<LifecycleStage, BadgeTone> = {
  opportunity: 'neutral',
  viability: 'info',
  'capital-raising': 'info',
  acquisition: 'neutral',
  construction: 'neutral',
  'sale-and-close': 'neutral',
};

export function PortalOpportunitiesPage() {
  const t = useT();
  /* The pipeline, with each note in the reader's language. The development's own
     name and its address are proper nouns and stay as they are. */
  const opportunities = useOpportunities();
  const future = opportunities.filter((opportunity) => FUTURE_STAGES.has(opportunity.stage));

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {t('shell.portal.tagline')}
        </p>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('portal.opportunities.title')}
        </h1>
        <p className="max-w-2xl text-sm text-body">{t('portal.opportunities.intro')}</p>
      </header>

      <InlineAlert
        tone="info"
        title={t('portal.opportunities.alertTitle')}
        description={t('portal.opportunities.alertBody')}
      />

      {future.length === 0 ? (
        <EmptyState
          variant="cards"
          title={t('portal.opportunities.emptyTitle')}
          description={t('portal.opportunities.emptyBody')}
        />
      ) : (
        <ul className="flex max-w-3xl list-none flex-col gap-4 p-0">
          {future.map((opportunity) => (
            <li
              key={opportunity.id}
              className="flex flex-col gap-3 rounded-xs border border-line bg-surface p-5 shadow-hard-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h2 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
                  {opportunity.name}
                </h2>
                <Badge tone={STAGE_TONES[opportunity.stage]} variant="subtle" size="sm">
                  {t(STAGE_LABELS[opportunity.stage])}
                </Badge>
              </div>

              <p className="inline-flex items-center gap-2 text-sm text-muted">
                <IconMapPin size={14} aria-hidden="true" className="shrink-0" />
                {opportunity.address} · {opportunity.city}
              </p>

              <p className="inline-flex items-center gap-2 text-sm text-muted">
                <IconCalendarEvent size={14} aria-hidden="true" className="shrink-0" />
                {t('portal.opportunities.projectedStart', {
                  date: formatDate(opportunity.projectedStart),
                })}
              </p>

              <p className="text-sm text-body">{opportunity.note}</p>

              <p className="inline-flex items-center gap-2 text-xs text-muted">
                <IconBuildingCommunity size={14} aria-hidden="true" className="shrink-0" />
                {t('portal.opportunities.notOpen')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
